import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getFinishedLecturesToReview } from 'redux/api/class';
import {
  Container,
  ExamCard,
  InfoRow,
  Label,
  Value,
  ReviewButton,
  EmptyState,
  LoadingContainer,
  Spinner,
} from './styles';

type RouteParams = {
  courseId: string;
  courseClassId: string;
};
const FinishedLectureList = ({
  classId,
  handleReview,
}: {
  classId: string;
  handleReview: (idAula: number, idProva: number) => void;
}) => {
  const { courseId } = useParams<RouteParams>();
  const [finishedLectures, setFinishedLectures] = useState<ExamsType[]>([]);
  const [loading, setLoading] = useState(true);

  const getFinishedLectures = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getFinishedLecturesToReview(classId, courseId);
      if (response && response?.exams) {
        setFinishedLectures(response?.exams);
      }
    } catch (error) {
      console.error('Erro ao buscar provas finalizadas:', error);
    } finally {
      setLoading(false);
    }
  }, [classId, courseId]);

  useEffect(() => {
    getFinishedLectures();
  }, [getFinishedLectures]);

  if (loading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (finishedLectures.length === 0) {
    return <EmptyState>Nenhuma prova finalizada encontrada.</EmptyState>;
  }

  return (
    <Container>
      {finishedLectures.map((exam) => (
        <ExamCard key={exam.id_prova} onClick={() => handleReview(exam.id_prova, exam.id_aula)}>
          <InfoRow>
            <Label>Data:</Label>
            <Value>{formatDate(exam.data_prova)}</Value>
          </InfoRow>
          <InfoRow>
            <Label>Hora:</Label>
            <Value>{formatTime(exam.data_prova)}</Value>
          </InfoRow>
          <InfoRow>
            <Label>Respondidas:</Label>
            <Value>{exam.total_questions}</Value>
          </InfoRow>
          <InfoRow>
            <Label>Acertos:</Label>
            <Value>{exam.correct_answers}</Value>
          </InfoRow>
          <InfoRow>
            <Label>Média de acertos:</Label>
            <Value>{Math.round(exam.score_percentage || 0)}%</Value>
          </InfoRow>
          <InfoRow>
            <Label>Duração:</Label>
            <Value>{exam.tempo_prova}</Value>
          </InfoRow>
          <ReviewButton>
            Fazer a revisão
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </ReviewButton>
        </ExamCard>
      ))}
    </Container>
  );
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

type ExamsType = {
  correct_answers: number;
  data_prova: string;
  id_aula: number;
  id_prova: number;
  id_usuario: number;
  score_percentage: number;
  tag_curso: string;
  tempo_prova: string;
  total_questions: number;
  total_score: number;
};

export default FinishedLectureList;
